"use client";

import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthStore } from "@/stores/authStore";
import { api } from "@/lib/axios";
import { apiBaseURL } from "@/lib/api-config";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, accessToken } = useAuth();
  const setSession = useAuthStore((s) => s.setSession);
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("admin@retailpro.local");
  const [password, setPassword] = useState("Admin@123");
  const [companyName, setCompanyName] = useState("");
  const [gstin, setGstin] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (accessToken) {
      navigate("/dashboard", { replace: true });
    }
  }, [accessToken, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "signin") {
        await login(email, password);
        navigate("/dashboard", { replace: true });
      } else {
        const { data } = await api.post<{
          user: unknown;
          access_token: string;
          refresh_token: string;
        }>("/api/auth/signup", {
          company_name: companyName,
          gstin,
          address,
          phone,
          email,
          password,
        });

        setSession({
          user: data.user as any,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });
        navigate("/dashboard", { replace: true });
      }
    } catch (err: unknown) {
      let msg = "Invalid email or password.";
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const raw = err.response?.data;
        const data = raw as { error?: string; message?: string; title?: string; messages?: unknown } | undefined;
        const nestedMsg =
          data &&
          typeof data.messages === "object" &&
          data.messages !== null &&
          "error" in data.messages &&
          typeof (data.messages as { error?: string }).error === "string"
            ? (data.messages as { error: string }).error
            : undefined;
        const fromBody =
          (typeof raw === "string" && raw.trim().startsWith("{")
            ? (() => {
                try {
                  const o = JSON.parse(raw) as { error?: string; message?: string };
                  return o.error ?? o.message;
                } catch {
                  return undefined;
                }
              })()
            : undefined) ??
          data?.error ??
          nestedMsg ??
          data?.message ??
          data?.title;
        if (fromBody) {
          msg = String(fromBody);
        } else if (status === 409 && mode === "signup") {
          msg = "Email already registered.";
        } else if ((status === 500 || status === 503 || status === 502) && !fromBody) {
          msg = "Server error. Check backend logs (writable/logs) and DB connection.";
        } else if (status === 401 || status === 403) {
          msg = data?.error ?? "Invalid email or password.";
        } else if (!err.response) {
          msg =
            apiBaseURL === ""
              ? "Cannot reach API. For dev: run `php spark serve` and keep Vite proxy, or set VITE_PUBLIC_API_URL in .env.local to your API (e.g. https://invoiceapi…/public). For production: set VITE_PUBLIC_API_URL at build time and fix CORS on the API."
              : "Cannot reach API (network/CORS). Check API URL, HTTPS, and that the server allows this site origin in CORS.";
        }
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-surface px-4 py-6 pb-safe">
      <div
        className="pointer-events-none absolute inset-0 opacity-95"
        style={{
          background:
            "radial-gradient(1200px circle at 10% 10%, hsl(239.2 84% 67.2% / 0.18), transparent 55%), radial-gradient(900px circle at 90% 20%, hsl(222.2 47.4% 11.2% / 0.06), transparent 50%), linear-gradient(180deg, hsl(210 40% 98%) 0%, hsl(214.3 31.8% 96.4%) 100%)",
        }}
      />
      <Card className="relative z-10 w-full max-w-md border-border/80 shadow-xl backdrop-blur-sm">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-xs font-bold leading-tight text-primary-foreground">
            inv
          </div>
          <CardTitle className="text-2xl font-semibold tracking-tight">
            invoice.cprawat.com
          </CardTitle>
          <CardDescription>
            {mode === "signin" ? "Sign in to your workspace" : "Create new company workspace"}
          </CardDescription>
          <Link
            to="/"
            className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            ← Back to marketing site
          </Link>
        </CardHeader>
        <CardContent>
          <Tabs
            value={mode}
            onValueChange={(v) => setMode(v === "signup" ? "signup" : "signin")}
            className="flex flex-col gap-4"
          >
            <TabsList variant="line" className="w-full justify-start gap-2 overflow-x-auto rounded-none p-0">
              <TabsTrigger value="signin" className="px-3 py-2">
                Sign in
              </TabsTrigger>
              <TabsTrigger value="signup" className="px-3 py-2">
                Sign up
              </TabsTrigger>
            </TabsList>

            <form onSubmit={onSubmit} className="space-y-4">
              <TabsContent value="signup" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company name</Label>
                  <Input
                    id="company_name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required={mode === "signup"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gstin">GSTIN</Label>
                  <Input id="gstin" value={gstin} onChange={(e) => setGstin(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    rows={3}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
              </TabsContent>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (mode === "signin" ? "Signing in…" : "Creating…") : mode === "signin" ? "Sign in" : "Create company"}
              </Button>
            </form>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
